import * as Sentry from '@sentry/browser';
import { instagramBoxConfig } from './config';
import { getInstagramPosts, InstagramPost } from './client';

const extractMagazineCode = (element: HTMLElement): string => {
  return element.dataset.code || '';
};

const getMagazineSettings = (magazineCode: string) => {
  return instagramBoxConfig.find((config) => config.magazineId === magazineCode);
};

const handleConfigurationError = (magazineCode: string) => {
  Sentry.captureException(
    `InstagramBox: Chyba při načítání konfigurace pro projekt s kódem ${magazineCode}`
  );
};

const createPostElement = (post: InstagramPost): HTMLAnchorElement => {
  const imageUrl = post.media_type === 'VIDEO' ? post.thumbnail_url : post.media_url;

  const postElement = document.createElement('a');
  postElement.classList.add('ig-post');
  postElement.setAttribute('href', post.permalink);
  postElement.setAttribute('data-ig-post', '');

  if (imageUrl) {
    const imageElement = document.createElement('img');
    imageElement.classList.add('ig-post__image');
    imageElement.setAttribute('src', imageUrl);
    imageElement.setAttribute('alt', '');

    postElement.appendChild(imageElement);
  } else {
    Sentry.captureException(`InstagramBox: Chybí URL obrázku pro Instagram příspěvek (${post.id})`);
  }

  return postElement;
};

const handlePostsContainerError = (container: HTMLElement) => {
  const magazineCode = extractMagazineCode(container);
  Sentry.captureException(
    `InstagramBox: Element pro zobrazení Instagram příspěvků nenalezen pro magazine s kódem ${magazineCode}`
  );
};

const handleNoPostsError = (container: HTMLElement) => {
  const magazineCode = extractMagazineCode(container);
  Sentry.captureException(
    `InstagramBox: Na Instagramu nejsou k dispozici žádné příspěvky pro magazine s kódem ${magazineCode}`
  );
};

const renderInstagramPosts = (posts: InstagramPost[], container: HTMLElement): void => {
  const postsContainer = container.querySelector('[data-ig-posts]');

  if (!postsContainer) {
    handlePostsContainerError(container);
    return;
  }

  if (posts.length === 0) {
    handleNoPostsError(container);
    return;
  }

  posts.forEach((post) => {
    const postElement = createPostElement(post);
    postsContainer.appendChild(postElement);
  });
};

export const InstagramBox = async (instagramBoxElement: HTMLElement) => {
  try {
    const magazineCode = extractMagazineCode(instagramBoxElement);
    const settings = getMagazineSettings(magazineCode);

    if (!settings) {
      handleConfigurationError(magazineCode);
      return;
    }

    const { userId, params } = settings;
    const latestPosts = await getInstagramPosts(userId, params);

    renderInstagramPosts(latestPosts, instagramBoxElement);
  } catch (err: unknown) {
    const errorMessage = err instanceof Error ? err.message : String(err);
    if (errorMessage) {
      Sentry.captureException(`InstagramBox: Unexpected error - ${errorMessage}`);
    }
  }
};
